import { AlertCircle, BarChart3, ChevronRight, Plus, Sparkles } from "lucide-react";
import React from "react";

import { Button } from "@/app/components/ui/button";

import { UPSSELL_RESHOW_DAYS } from "./constants";
import { useCompanyDashboard } from "./CompanyDashboardProvider";
import { ContentSection, ShareActions } from "./shared";

function SetupChecklistPanel({ items, completedCount, totalCount, progressPercentage, onNavigate }) {
	return (
		<div className="rounded-lg border border-border bg-white p-5">
			<div className="mb-5">
				<div className="mb-2 flex items-center justify-between">
					<h3 className="text-base font-semibold text-foreground">Complete Your Profile</h3>
					<span className="text-sm font-medium text-muted-foreground">
						{completedCount} of {totalCount} complete
					</span>
				</div>
				<div className="h-2 overflow-hidden rounded-full bg-secondary">
					<div className="company-dashboard-gradient h-full rounded-full transition-all duration-500" style={{ width: `${progressPercentage}%` }} />
				</div>
				<p className="mt-2 text-xs text-muted-foreground">
					{progressPercentage}% complete. Finish the remaining items to unlock the full dashboard experience.
				</p>
			</div>

			<div className="space-y-2">
				{items.map((item) => (
					<button
						key={item.id}
						type="button"
						disabled={item.completed}
						onClick={() => !item.completed && onNavigate(item.targetTab, item.id)}
						className={`flex w-full items-center gap-3 rounded-xl p-3 text-left transition-colors ${
							item.completed ? "bg-green-50" : "hover:bg-secondary"
						}`}
					>
						<div className={`flex h-8 w-8 items-center justify-center rounded-full ${item.completed ? "bg-green-100" : "bg-gray-100"}`}>
							{item.completed ? <span className="text-sm font-semibold text-green-700">✓</span> : <span className="text-sm font-semibold text-gray-500">•</span>}
						</div>
						<div className="min-w-0 flex-1">
							<p className={`text-sm font-medium ${item.completed ? "text-green-700" : "text-foreground"}`}>{item.label}</p>
							<p className="text-xs text-muted-foreground">{item.description}</p>
						</div>
						{!item.completed ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : null}
					</button>
				))}
			</div>
		</div>
	);
}

function StatCard({ label, value, tint }) {
	return (
		<div className="rounded-lg border border-border bg-white p-4">
			<p className="text-sm font-medium text-muted-foreground">{label}</p>
			<p className={`mt-3 text-3xl font-bold ${tint}`}>{value.toLocaleString()}</p>
		</div>
	);
}

function PendingActions({ jobs, onNavigate }) {
	const expiringJobs = jobs.filter((job) => job.status === "open" && job.deadline && dayDiff(job.deadline) <= 7 && dayDiff(job.deadline) >= 0);
	const actionItems = [];

	if (expiringJobs.length > 0) {
		actionItems.push({
			id: "expiring",
			title: `${expiringJobs.length} role${expiringJobs.length > 1 ? "s" : ""} expiring soon`,
			subtitle: "Review deadlines or extend them from the jobs tab.",
			actionLabel: "Review",
			targetTab: "jobs",
		});
	}

	if (jobs.some((job) => job.status === "open" && job.views > 0)) {
		actionItems.push({
			id: "traffic",
			title: "Fresh traffic on your open roles",
			subtitle: "Check the activity timeline for new views and candidate actions.",
			actionLabel: "Open activity",
			targetTab: "activity",
		});
	}

	if (!actionItems.length) {
		return (
			<div className="rounded-lg border border-border bg-white p-6 text-center">
				<div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-50">
					<AlertCircle className="h-5 w-5 text-green-700" />
				</div>
				<p className="text-sm font-medium text-foreground">You're all caught up</p>
				<p className="mt-1 text-sm text-muted-foreground">New company activity will appear here as it happens.</p>
			</div>
		);
	}

	return (
		<div className="rounded-lg border border-border bg-white p-5">
			<div className="mb-4 flex items-center justify-between">
				<h3 className="text-base font-semibold text-foreground">Pending Actions</h3>
				<span className="rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700">{actionItems.length} pending</span>
			</div>
			<div className="space-y-3">
				{actionItems.map((item) => (
					<button
						key={item.id}
						type="button"
						onClick={() => onNavigate(item.targetTab)}
						className="flex w-full items-center justify-between rounded-xl border border-border bg-white p-4 text-left transition-colors hover:border-primary-200 hover:bg-secondary/40"
					>
						<div>
							<p className="text-sm font-semibold text-foreground">{item.title}</p>
							<p className="mt-1 text-xs text-muted-foreground">{item.subtitle}</p>
						</div>
						<span className="text-sm font-medium text-primary-700">{item.actionLabel}</span>
					</button>
				))}
			</div>
		</div>
	);
}

function dayDiff(dateString) {
	return Math.ceil((new Date(dateString).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

export default function HomeTab() {
	const {
		companyId,
		companyName,
		stats,
		jobs,
		checklist,
		goToTab,
		goToUpgrade,
		previewCompanyPage,
		shareCompanyPage,
		copyEmbedCode,
		upsellDismissedAt,
		dismissUpsell,
		resetUpsell,
	} = useCompanyDashboard();

	React.useEffect(() => {
		if (!upsellDismissedAt) return;
		const dismissedAt = new Date(upsellDismissedAt);
		const daysSinceDismissal = Math.floor((Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60 * 24));
		if (daysSinceDismissal >= UPSSELL_RESHOW_DAYS) {
			resetUpsell();
		}
	}, [resetUpsell, upsellDismissedAt]);

	const title = checklist.isSetupComplete ? `Welcome back, ${companyName}` : `Manage, ${companyName}`;
	const subtitle = checklist.isSetupComplete
		? "Monitor job performance, keep the company profile current, and review activity."
		: "Finish the key company profile steps so the public Dproz company page looks complete and trustworthy.";

	return (
		<ContentSection
			title={title}
			subtitle={subtitle}
			actions={<ShareActions companyId={companyId} companyName={companyName} onCopyEmbed={copyEmbedCode} onShare={shareCompanyPage} onPreview={previewCompanyPage} />}
		>
			<div className="space-y-6">
				<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
					<StatCard label="Open Jobs" value={stats.openJobs} tint="text-primary-700" />
					<StatCard label="Closed Roles" value={stats.closedJobs} tint="text-green-700" />
					<StatCard label="Total Views" value={stats.totalViews} tint="text-purple-700" />
					<StatCard label="Total Candidates" value={stats.totalCandidates} tint="text-orange-700" />
				</div>

				{!checklist.isSetupComplete ? (
					<div className="grid gap-6 xl:grid-cols-[1.6fr_1fr]">
						<SetupChecklistPanel
							items={checklist.items}
							completedCount={checklist.completedCount}
							totalCount={checklist.totalCount}
							progressPercentage={checklist.progressPercentage}
							onNavigate={goToTab}
						/>
						<div className="company-dashboard-subtle rounded-lg border border-primary-100 p-6">
							<h3 className="text-lg font-semibold text-foreground">Keep momentum</h3>
							<p className="mt-2 text-sm text-muted-foreground">
								Once the core company details are in place, the branding and activity tabs become much more useful.
							</p>
							<div className="mt-6 flex flex-col gap-3">
								<Button onClick={() => goToTab("branding")}>
									Start Branding
								</Button>
								<Button variant="outline" onClick={() => goToTab("jobs")}>
									<Plus className="mr-2 h-4 w-4" />
									Review Jobs
								</Button>
							</div>
						</div>
					</div>
				) : (
					<div className="grid gap-6 xl:grid-cols-[1.6fr_1fr]">
						<PendingActions jobs={jobs} onNavigate={goToTab} />
						<div className="rounded-lg border border-border bg-white p-6">
							<h3 className="text-base font-semibold text-foreground">Quick Actions</h3>
							<div className="mt-4 grid gap-3">
								<Button onClick={() => goToTab("jobs")}>
									<Plus className="mr-2 h-4 w-4" />
									Review Jobs
								</Button>
								<Button variant="outline" onClick={() => goToTab("branding")}>
									Refresh Branding
								</Button>
								<Button variant="outline" onClick={() => goToTab("activity")}>
									<BarChart3 className="mr-2 h-4 w-4" />
									Open Activity
								</Button>
							</div>
						</div>
					</div>
				)}

				{!upsellDismissedAt ? (
					<div className="company-dashboard-gradient relative overflow-hidden rounded-lg p-6 text-white">
						<div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-white/10" />
						<div className="absolute -bottom-8 left-8 h-24 w-24 rounded-full bg-white/10" />
						<div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
							<div className="max-w-2xl">
								<h3 className="text-lg font-semibold">Upgrade your employer presence</h3>
								<p className="mt-1 text-sm text-white/85">
									Corporate and Enterprise tiers unlock richer branding controls, more activity visibility, and a more polished employer experience.
								</p>
							</div>
							<div className="flex gap-3">
								<Button className="bg-white text-primary-700 hover:bg-white/90" onClick={goToUpgrade}>
									<Sparkles className="mr-2 h-4 w-4" />
									View Plans
								</Button>
								<Button variant="outline" className="border-white text-white hover:bg-white/10" onClick={dismissUpsell}>
									Dismiss
								</Button>
							</div>
						</div>
					</div>
				) : null}
			</div>
		</ContentSection>
	);
}
